import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Loader2, ArrowLeft, Pencil, UserRound } from "lucide-react";
import { api, formatApiError } from "@/api/client";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { usePipelineMeta, StageBadge, ChecklistPill, DocStatusBadge } from "@/lib/pipeline";
import { CandidateForm } from "@/components/CandidateForm";
import { CandidateDocs } from "@/components/CandidateDocs";
import { CandidateChecklist } from "@/components/CandidateChecklist";
import { useAuth } from "@/context/AuthContext";
import { PendingBanner } from "@/components/PipelineAlerts";
import { TagList } from "@/components/MultiTagSelect";
import { SeaProfile } from "@/components/SeaProfile";
import { ProposalSubmit } from "@/components/ProposalSubmit";

function Photo({ c }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let url;
    if (c.has_photo) api.get(`/candidates/${c.id}/photo`, { responseType: "blob" }).then(({ data }) => { url = URL.createObjectURL(data); setSrc(url); }).catch(() => {});
    return () => url && URL.revokeObjectURL(url);
  }, [c.id, c.has_photo]);
  return src ? <img src={src} alt="" className="w-20 h-20 rounded-xl object-cover border border-slate-200" data-testid="candidate-photo" />
    : <div className="w-20 h-20 rounded-xl bg-slate-100 flex items-center justify-center"><UserRound className="w-8 h-8 text-slate-300" /></div>;
}

const addr = (a) => a ? [a.house, a.area, a.landmark, a.district, a.state, a.pin_code, a.country].filter(Boolean).join(", ") : "";

function Overview({ c }) {
  const rows = [["Ranks", <TagList items={c.ranks} testid="candidate-ranks" />], ["Vessel types sailed", <TagList items={c.vessel_types} testid="candidate-vessel-types" />],
    ["CDC number", c.cdc_number], ["Passport", c.passport_number], ["Passport expiry", c.passport_expiry],
    ["Availability", c.availability_status], ["Mobile", c.phone], ["Email", c.email], ["Address", addr(c.address)],
    ["Next of kin", c.nok?.name ? `${c.nok.name}${c.nok.relation ? ` (${c.nok.relation})` : ""}` : ""], ["NOK mobile", c.nok?.mobile],
    ["NOK address", addr(c.nok?.address)], ["Source", c.source]];
  const active = c.assignments.find((a) => a.is_active);
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="candidate-details">
        <div className="text-sm font-semibold text-slate-800 mb-3">Personal details</div>
        <dl className="grid grid-cols-[130px_1fr] gap-y-2 text-sm">{rows.map(([k, v]) => <><dt key={k} className="text-slate-400">{k}</dt><dd className="text-slate-800 break-words">{v || "—"}</dd></>)}</dl>
      </div>
      <div className="space-y-4">
        <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="candidate-assignment">
          <div className="text-sm font-semibold text-slate-800 mb-2">Current vessel assignment</div>
          {active ? <div className="text-sm text-slate-700"><Link to={`/requirements/${active.requirement_id}`} className="font-medium hover:text-cyan-700">{active.vessel_name} · {active.rank}</Link>
            <div className="text-slate-500 mt-1">Interview {active.interview_date || "—"} · <StageBadge stage={active.current_stage} /></div></div>
            : <div className="text-sm text-slate-400">Not assigned to a requirement.</div>}
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="candidate-sea-list">
          <div className="text-sm font-semibold text-slate-800 mb-2">SEA contracts</div>
          {c.sea_contracts.length ? c.sea_contracts.map((s) => <div key={s.id} className="text-sm text-slate-700">{s.vessel_name} · {s.owner_name} · {new Date(s.generated_at).toLocaleDateString()} <Link to="/sea-contracts" className="text-cyan-700 ml-1">open list</Link></div>)
            : <div className="text-sm text-slate-400">None yet.</div>}
        </div>
      </div>
      <div className="bg-white border border-slate-200 rounded-xl p-5 lg:col-span-2" data-testid="candidate-history">
        <div className="text-sm font-semibold text-slate-800 mb-3">Stage history</div>
        <ol className="relative border-l border-slate-200 ml-2 space-y-4">
          {c.history.map((h, i) => (
            <li key={i} className="ml-4">
              <span className="absolute -left-1.5 w-3 h-3 rounded-full bg-cyan-600 border-2 border-white" />
              <div className="flex flex-wrap items-center gap-2 text-sm"><StageBadge stage={h.to_stage} /><span className="text-slate-500">{h.note}</span></div>
              <div className="text-xs text-slate-400 mt-0.5">{new Date(h.at).toLocaleString()} · {h.by_name}</div>
            </li>
          ))}
        </ol>
      </div>
      {c.audit?.length > 0 && (
        <div className="bg-white border border-rose-200 rounded-xl p-5 lg:col-span-2" data-testid="candidate-audit">
          <div className="text-sm font-semibold text-slate-800 mb-3">Audit log</div>
          <ul className="space-y-2">
            {c.audit.map((x, i) => (
              <li key={i} className="text-sm" data-testid={`audit-entry-${i}`}>
                <span className="font-medium text-rose-700">{x.action}</span> <span className="text-slate-600">— {x.reason}</span>
                <div className="text-xs text-slate-400">{new Date(x.at).toLocaleString()} · {x.by_name}</div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export default function CandidateProfilePage() {
  const { id } = useParams();
  const { user } = useAuth();
  const meta = usePipelineMeta();
  const [c, setC] = useState(null);
  const [edit, setEdit] = useState(false);
  const [pinfo, setPinfo] = useState(null);
  const load = () => api.get(`/candidates/${id}`).then(({ data }) => setC(data)).catch((err) => { toast.error(formatApiError(err)); setC(false); });
  const loadP = () => api.get(`/candidates/${id}/proposal`).then(({ data }) => setPinfo({ ...data, cid: id })).catch(() => {});
  useEffect(() => { load(); loadP(); }, [id]); // eslint-disable-line

  if (c === null || !meta) return <div className="py-20 text-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400 inline" /></div>;
  if (c === false) return <div className="py-20 text-center text-slate-500" data-testid="candidate-not-found">Candidate not found.</div>;
  const caps = meta.caps;
  const own = c.created_by === user?.id;
  const canEdit = caps.full || caps.manager || (caps.is_sourcing && own);
  const canUpload = caps.full || caps.docs || caps.manager || caps.office_admin || (caps.is_sourcing && own);
  return (
    <div className="space-y-6" data-testid="candidate-profile-page">
      <Link to="/candidates" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800"><ArrowLeft className="w-4 h-4" /> Candidates</Link>
      <div className="flex flex-wrap items-center gap-4">
        <Photo c={c} />
        <div className="flex-1 min-w-0">
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: "Manrope" }} data-testid="candidate-name">{c.full_name}</h1>
          <div className="flex flex-wrap items-center gap-2 mt-1.5 text-sm text-slate-500">
            {c.rank} · <StageBadge stage={c.current_stage} testid="candidate-current-stage" /> <ChecklistPill done={c.checklist_done} total={c.checklist_total} /> <DocStatusBadge status={c.doc_status} />
          </div>
        </div>
        {canEdit && <Button variant="outline" onClick={() => setEdit(true)} data-testid="edit-candidate-btn"><Pencil className="w-4 h-4 mr-1" /> Edit</Button>}
      </div>
      {caps.sees_alerts && <PendingBanner c={c} />}
      <Tabs defaultValue={caps.docs ? "documents" : "overview"}>
        <div className="max-w-full overflow-x-auto"><TabsList>
          <TabsTrigger value="overview" data-testid="candidate-tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="documents" data-testid="candidate-tab-documents">Documents</TabsTrigger>
          <TabsTrigger value="checklist" data-testid="candidate-tab-checklist">Checklist (57)</TabsTrigger>
          <TabsTrigger value="sea" data-testid="candidate-tab-sea-service">Sea Service</TabsTrigger>
          <TabsTrigger value="proposal" data-testid="candidate-tab-proposal">Proposal</TabsTrigger>
        </TabsList></div>
        <TabsContent value="overview" className="mt-4"><Overview c={c} /></TabsContent>
        <TabsContent value="documents" className="mt-4"><CandidateDocs candidate={c} meta={meta} canUpload={canUpload} onChanged={load} /></TabsContent>
        <TabsContent value="checklist" className="mt-4"><CandidateChecklist candidate={c} meta={meta} onChanged={load} /></TabsContent>
        <TabsContent value="sea" className="mt-4">{pinfo && <SeaProfile info={pinfo} meta={meta} onSaved={loadP} />}</TabsContent>
        <TabsContent value="proposal" className="mt-4">{pinfo && <ProposalSubmit info={pinfo} onChanged={loadP} />}</TabsContent>
      </Tabs>
      <CandidateForm open={edit} onOpenChange={setEdit} meta={meta} candidate={c} onSaved={load} />
    </div>
  );
}
